// src/components/molecules/CreateFolderModal/CreateFolderModal.tsx
import React, { useMemo, useCallback } from 'react';
import { z } from 'zod';
import { Container, Row, Col, Form } from 'shared-portal-component';

import AppModal from '@/components/common/AppModal/AppModal';

export interface CreateFolderModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (folderName: string) => void;
  currentPath: string;
  existingFolderNames: string[];
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  open,
  onClose,
  onConfirm,
  currentPath,
  existingFolderNames,
}) => {
  // Build validation schema with duplicate check
  const schema = useMemo(() => {
    const lowerCaseNames = existingFolderNames.map(name => name.toLowerCase());

    return z.object({
      folderName: z
        .string()
        .min(1, 'Folder name is required')
        .refine(
          (name) => !name.includes('/') && !name.includes('\\'),
          'Folder name cannot contain slashes'
        )
        .refine(
          (name) => !lowerCaseNames.includes(name.trim().toLowerCase()),
          'A folder with this name already exists'
        ),
    });
  }, [existingFolderNames]);

  const fields = useMemo(() => [
    {
      name: 'folderName',
      placeholder: 'Enter folder name',
      required: true,
      variant: 'outline' as const,
      type: 'text',
    },
  ], []);

  const handleSubmit = useCallback((values: { folderName: string }) => {
    onConfirm(values.folderName.trim());
  }, [onConfirm]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  // Format path for display
  const displayPath = currentPath === '/' ? 'Root' : currentPath.replace(/\/$/, '');

  return (
    <AppModal onClose={handleClose} title="Create New Folder" size="md">
      <Container fluid>
        <Row spacing="md" justify="center">
          <Col size={12} className="text-center mb-2">
            <p className="text-sm text-gray-600">
              Creating folder in: <span className="font-medium">{displayPath}</span>
            </p>
          </Col>
        </Row>
        <Row spacing="md" justify="center">
          <Col md={10} sm={12}>
            <Form
              schema={schema}
              fields={fields}
              onSubmit={handleSubmit}
              defaultValues={{ folderName: '' }}
              submitText="Create Folder"
            />
          </Col>
        </Row>
      </Container>
    </AppModal>
  );
};

export default CreateFolderModal;
